import {regNavn} from "felles/tjenester/tredjepartsTjenestenavn";
import {Konfigurasjon} from "felles/infrastruktur/Konfigurasjon";
import {IAngularEvent, ILogService, IRootScopeService, IScope, ITimeoutService} from "angular";
import {l} from "types/typescriptdefinisjoner_lederplan";
import {konverterIso8601DatoStrengTilDate} from "felles/infrastruktur/Iso8601DatostrengTilDate";
import type {AtmosphereRequest, AtmosphereResponse, Subscription, SubscriptionHandlers} from "atmosphere.js"
import {Atmosphere} from "atmosphere.js"
import {Debugger} from "debug";
import _ from "lodash";
import {filter, Observable, Subject} from "rxjs";
import {Modelloppdatering, ModelloppdateringType} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {AjsModelloppdateringTjeneste} from "felles-ng2/tjenester/ajs/AjsModelloppdateringTjeneste";


const debug: Debugger = require('debug')('ModelloppdateringTjeneste');

/** Håndtere opprettelse av web-socket for abonnement på modellendringer fra serveren.
 * Se lederplan-tjenester/src/main/java/no/systemfabrikken/lederplan/tjenester/pushmeldinger/WebPushModellOppdatertEndepunkt.java for endepunktet som håndtere websocketen */
export class ModelloppdateringTjeneste implements AjsModelloppdateringTjeneste {

    static regNavn = 'ModelloppdateringTjeneste';

    static forInjisering() {
        return [
            regNavn.ng.$rootScope,
            regNavn.ng.$log,
            regNavn.ng.$timeout,
            Konfigurasjon.regNavn,
            ModelloppdateringTjeneste
        ]
    }

    constructor(private $rootScope: IRootScopeService,
                private $log: ILogService,
                private $timeout: ITimeoutService,
                private konfigurasjon: Konfigurasjon) {
    }

    /** Aktiv atmosphere instans*/
    private atmosphereInstans:Atmosphere|null = null;
    /** Sann dersom vi eksplisitt har lukket atmosphere instansen */
    private atmosphereLukket = true;
    /** Subject som alle hendelser vi mottar via atmosphere blir publiser til */
    private _hendelser = new Subject<Modelloppdatering<any>>();

    //offentlige metoder

    //todo ved oppdatering av brukers org må abonnement restartes
    async startAbonnement() {
        debug('starter abonnement');
        await this.registrerAbonnement();
    }

    async avsluttAbonnement() {
        debug('avslutter abonnement');
        await this.terminerAbonnement();
    }

    get hendelser(): Subject<Modelloppdatering<any>> {
        return this._hendelser;
    }

    lyttTilHendelse<HENDELSE_MODELL_TYPE>(type: ModelloppdateringType): Observable<Modelloppdatering<HENDELSE_MODELL_TYPE>> {
        return this.hendelser.pipe(filter(hendelse => hendelse.type == type));
    }

    /**
     * Broadcaster at en modelloppdatering identifisert ved angitt eventId
     * har funnet sted, angitt modell representerer den oppdaterte modellen.
     * eventId må være en av de statisk-definerte event_ verdiene.
     *
     * Det er foretrukket å bruke de eventspesifikke broadcastLokal...(..) metodene
     * for enklere å sikre at modellen blir korrekt.
     */
    broadcastLokalModellOppdatert(eventId: string, modell: any) {
        this.broadcastModellOppdatert(eventId, modell)
    }

    // plan
    vedPlanOppdatert(filter: (oppdatertPlanId: string) => boolean, scope: IScope, vedOppdatering: (oppdatertPlanId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.PLAN_OPPDATERT);
    }

    broadcastLokalOppdatertPlanId(oppdatertPlanId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.PLAN_OPPDATERT, oppdatertPlanId);
    }

    //avtale

    vedAvtaleOppdatert(filter: (oppdatertAvtaleId: string) => boolean, scope: IScope, vedOppdatering: (oppdatertAvtaleId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.AVTALE_OPPDATERT);
    }

    vedAvtaleAvtaleTerminert(filter: (terminertAvtaleId: string) => boolean, scope: IScope, vedOppdatering: (terminertAvtaleId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.AVTALE_AVTALE_TERMINERT);
    }

    //gruppe

    vedGruppeOppdatert(filter: (oppdatertGruppeId: string) => boolean, scope: IScope, vedOppdatering: (oppdatertGruppeId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.GRUPPE_OPPDATERT);
    }

    broadcastLokalGruppeOppdatert(gruppeId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.GRUPPE_OPPDATERT, gruppeId);
    }

    //møte

    vedNyttMøte(filter: (nyMøteId: string) => boolean, scope: IScope, vedOppdatering: (nyMøteId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.NYTT_MØTE);
    }

    broadcastLokalNyttMøte(nyttMøteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.NYTT_MØTE, nyttMøteId);
    }

    vedOppdatertMøte(filter: (oppdatertMøteId: string) => boolean, scope: IScope, vedOppdatering: (oppdatertMøteId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.MØTE_OPPDATERT);
    }

    vedSlettetMøte(filter: (slettetMøteId: string) => boolean, scope: IScope, vedOppdatering: (slettetMøteId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.MØTE_SLETTET);
    }

    broadcastLokalSlettetMøte(slettetMøteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.MØTE_SLETTET, slettetMøteId);
    }

    vedMøteAvstemningÅpnet(filter: (oppdatertMøteId: string) => boolean, scope: IScope, vedOppdatering: (oppdatertMøteId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.MØTE_AVSTEMNING_ÅPNET);
    }

    broadcastLokalMøteAvstemningÅpnet(møteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.MØTE_AVSTEMNING_ÅPNET, møteId);
    }

    vedMøteAvstemningStengt(filter: (oppdatertMøtId: string) => boolean, scope: IScope, vedOppdatering: (oppdatertMøteId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.MØTE_AVSTEMNING_STENGT);
    }

    broadcastLokalMøteAvstemningStengt(møteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.MØTE_AVSTEMNING_STENGT, møteId);
    }

    //møtedeltakere

    vedMøtedeltakereOppdatert(filter: (moeteId: string) => boolean, scope: IScope, vedOppdatering: (moeteId: string) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.MØTEDELTAKERE_OPPDATERT);
    }

    broadcastLokalMøtedeltakereOppdatert(deltakere: l.MøtedeltakereModell, møteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.MØTEDELTAKERE_OPPDATERT, møteId);
    }

    vedMøtedeltakerAvstemningsresultatNullstilt(filter: (_: { moetedeltakerId: string, moeteId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        moetedeltakerId: string;
        moeteId: string;
    }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.MØTEDELTAKER_AVSTEMNINGSRESULTAT_NULLSTILT);
    }

    broadcastLokalMøtedeltakerAvstemningsresultatNullstilt(moetedeltakerId: string, moeteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.MØTEDELTAKER_AVSTEMNINGSRESULTAT_NULLSTILT, {moetedeltakerId: moetedeltakerId, moeteId: moeteId});
    }

    vedMøtedeltakerAvstemningsresultatEndret(filter: (_: { moetedeltakerId: string, avstemningId: string, moeteId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        moetedeltakerId: string;
        avstemningId: string;
        moeteId: string;
    }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.MØTEDELTAKER_AVSTEMNINGSRESULTAT_ENDRET);
    }

    broadcastLokalMøtedeltakerAvstemningsresultatEndret(moetedeltakerId: string, avstemningId: string, moeteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.MØTEDELTAKER_AVSTEMNINGSRESULTAT_ENDRET, {moetedeltakerId: moetedeltakerId, avstemningId: avstemningId, moeteId: moeteId});
    }

    vedMøtedeltakerOppdatert(filter: (_: { moetedeltakerId: string, moeteId: string }) => boolean, scope: IScope, vedOppdatering: (_: { moetedeltakerId: string, moeteId: string }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.MØTEDELTAKER_OPPDATERT);
    }

    broadcastLokalMøtedeltakerOppdatert(oppdatertDeltakerId: string, møteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.MØTEDELTAKER_OPPDATERT, {moetedeltakerId: oppdatertDeltakerId, moeteId: møteId});
    }

    //protokollførtmøtedeltakere

    private finnMøteId(entitetReferanser: l.EntitetReferanseModell[]): string {
        return entitetReferanser.find(e => e.type === "Møte")!.id;
    }

    //sak

    vedNySak(filter: (_: { agendaElementId: string, moeteId: string }) => boolean, scope: IScope, vedOppdatering: (_: { agendaElementId: string; moeteId: string; }) => void) {
        const argMapper = (arg: l.GeneriskPushModell) => ({agendaElementId: arg.endretEntitet.id, moeteId: this.finnMøteId(arg.foreldre)});
        this.registrerLytter((arg: l.GeneriskPushModell) => (filter(argMapper(arg))), scope, (arg: l.GeneriskPushModell) => (vedOppdatering(argMapper(arg))), ModelloppdateringType.NY_SAK);
    }

    broadcastLokalSakFlyttetTilMøte(sak: l.AgendaelementModell, opprinneligMoeteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.SAK_FLYTTET_TIL_MØTE, {
            sakId: sak.id,
            moeteId: sak.moete.id,
            opprinneligMoeteId: opprinneligMoeteId
        });
    }

    vedSakReferatOppdatert(filter: (_: { sakId: string; moeteId: string }) => boolean, scope: IScope, vedOppdatering: (_: { sakId: string; moeteId: string; }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.SAK_REFERAT_OPPDATERT);
    }

    //avstemning

    vedNyAvstemning(filter: (_: { avstemningId: string, sakId: string }) => boolean, scope: IScope, vedOppdatering: (_: { avstemningId: string; sakId: string; }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.NY_AVSTEMNING);
    }

    broadcastLokalNyAvstemning(avstemning: l.AvstemningMedResultatModell) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.NY_AVSTEMNING, {avstemningId: avstemning.id, sakId: avstemning.sakId});
    }

    vedOppdatertAvstemning(filter: (_: { avstemningId: string, sakId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        avstemningId: string;
        sakId: string;
    }) => void, evalueringstidspunkt: Evalueringstipdspunkt = Evalueringstipdspunkt.umiddelbart) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.AVSTEMNING_OPPDATERT, evalueringstidspunkt);
    }

    broadcastLokalOppdatertAvstemning(avstemning: l.AvstemningMedResultatModell) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.AVSTEMNING_OPPDATERT, {avstemningId: avstemning.id, sakId: avstemning.sakId});
    }

    vedAvstemningÅpnet(filter: (_: { avstemningId: string, sakId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        avstemningId: string;
        sakId: string;
    }) => void, evalueringstidspunkt: Evalueringstipdspunkt = Evalueringstipdspunkt.umiddelbart) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.AVSTEMNING_ÅPNET, evalueringstidspunkt);
    }

    broadcastLokalAvstemningÅpnet(avstemning: l.AvstemningMedResultatModell) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.AVSTEMNING_ÅPNET, {avstemningId: avstemning.id, sakId: avstemning.sakId});
    }

    vedAvstemningStengt(filter: (_: { avstemningId: string, sakId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        avstemningId: string;
        sakId: string;
    }) => void, evalueringstidspunkt: Evalueringstipdspunkt = Evalueringstipdspunkt.umiddelbart) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.AVSTEMNING_STENGT, evalueringstidspunkt);
    }

    broadcastLokalAvstemningStengt(avstemning: l.AvstemningMedResultatModell) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.AVSTEMNING_STENGT, {avstemningId: avstemning.id, sakId: avstemning.sakId});
    }

    vedSlettetAvstemning(filter: (slettetAvstemningId: String) => boolean, scope: IScope, vedOppdatering: (slettetAvstemningId: String) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.AVSTEMNING_SLETTET);
    }

    broadcastLokalSlettetAvstemning(avstemningId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.AVSTEMNING_SLETTET, avstemningId);
    }

    //beslutning

    vedNyBeslutning(filter: (_: { beslutningId: string, sakId: string }) => boolean, scope: IScope, vedOppdatering: (_: { beslutningId: string, sakId: string }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.NY_BESLUTNING);
    }

    broadcastLokalNyBeslutning(beslutning: l.BeslutningModell) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.NY_BESLUTNING, {beslutningId: beslutning.id, sakId: beslutning.sakId});
    }

    vedOppdatertBeslutning(filter: (_: { beslutningId: string, sakId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        beslutningId: string;
        sakId: string;
    }) => void, evalueringstidspunkt: Evalueringstipdspunkt = Evalueringstipdspunkt.umiddelbart) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.BESLUTNING_OPPDATERT, evalueringstidspunkt);
    }

    broadcastLokalOppdatertBeslutning(beslutning: l.BeslutningModell) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.BESLUTNING_OPPDATERT, {beslutningId: beslutning.id, sakId: beslutning.sakId});
    }

    vedSlettetBeslutning(filter: (slettetBesluttningId: String) => boolean, scope: IScope, vedOppdatering: (slettetBesluttningId: String) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.BESLUTNING_SLETTET);
    }

    broadcastLokalSlettetBeslutning(beslutningId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.BESLUTNING_SLETTET, beslutningId);
    }

    //oppgave

    vedNyOppgave(filter: (_: { oppgaveId: string, oppgaveForelderId: string, moeteId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        oppgaveId: string;
        oppgaveForelderId: string;
        moeteId: string;
    }) => void, evalueringstidspunkt: Evalueringstipdspunkt = Evalueringstipdspunkt.umiddelbart) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.NY_OPPGAVE, evalueringstidspunkt);
    }

    broadcastLokalNyOppgave(oppgave: l.OppgaveModell, moeteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.NY_OPPGAVE, {oppgaveId: oppgave.id, oppgaveForelderId: oppgave.forelderRef?.id, moeteId: moeteId,});
    }

    vedOppdatertOppgave(filter: (_: { oppgaveId: string, moeteId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        oppgaveId: string;
        moeteId: string;
    }) => void, evalueringstidspunkt: Evalueringstipdspunkt = Evalueringstipdspunkt.umiddelbart) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.OPPGAVE_OPPDATERT, evalueringstidspunkt);
    }

    broadcastLokalOppdatertOppgave(oppgave: l.OppgaveModell, moeteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.OPPGAVE_OPPDATERT, {
            oppgaveId: oppgave.id,
            moeteId: moeteId
        });
    }

    vedSlettetOppgave(filter: (_: { oppgaveId: string, moeteId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        oppgaveId: string;
        moeteId: string;
    }) => void, evalueringstidspunkt: Evalueringstipdspunkt = Evalueringstipdspunkt.umiddelbart) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.OPPGAVE_SLETTET, evalueringstidspunkt);
    }

    broadcastLokalSlettetOppgave(oppgaveId: string, moeteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.OPPGAVE_SLETTET, {
            oppgaveId: oppgaveId,
            moeteId: moeteId
        });
    }

    //dokument

    broadcastLokalNyttDokument(dokument: l.SaksdokumentModell, moeteId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.NYTT_DOKUMENT, {
            dokumentId: dokument.dokumentId,
            dokumentVersjonId: dokument.id,
            sakId: dokument.foreldre.sakId,
            moeteId: moeteId
        });
    }

    vedDokumentVirussjekket(filter: (_: { dokumentVersjonId: string }) => boolean, scope: IScope, vedOppdatering: (_: { dokumentVersjonId: string }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.DOKUMENT_VIRUSSJEKKET);
    }

    vedDokumentKonvertertPdf(filter: (_: { dokumentVersjonId: string }) => boolean, scope: IScope, vedOppdatering: (_: { dokumentVersjonId: string }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.DOKUMENT_KONVERTERTPDF);
    }

    vedDokumentForsidebildeOpprettet(filter: (_: { dokumentVersjonId: string }) => boolean, scope: IScope, vedOppdatering: (_: { dokumentVersjonId: string }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.DOKUMENT_FORSIDEBILDEOPPRETTET);
    }

    vedDokumentTilSignering(filter: (_: { dokumentId: string }) => boolean, scope: IScope, vedOppdatering: (_: { dokumentId: string }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.DOKUMENT_TILSIGNERING);
    }

    vedDokumentKomplettSignert(filter: (_: { dokumentId: string }) => boolean, scope: IScope, vedOppdatering: (_: { dokumentId: string }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.DOKUMENT_KOMPLETTSIGNERT);
    }

    broadcastDokumentKomplettSignert(dokumentId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.DOKUMENT_KOMPLETTSIGNERT, {dokumentId: dokumentId});
    }

    vedDokumentLåstOpp(filter: (_: { dokumentId: string }) => boolean, scope: IScope, vedOppdatering: (_: { dokumentId: string }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.DOKUMENT_LÅSTOPP);
    }

    // Signatar

    vedSignatarSignert(filter: (_: { dokumentId: string, signatarId: string }) => boolean, scope: IScope, vedOppdatering: (_: { dokumentId: string, signatarId: string }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.SIGNATAR_SIGNERT);
    }

    broadcastLokalSignatarSignert(dokumentId: string, signatarId?: string) {
        //signatarId brukes ikke av de som per i dag lytter til denne event - derfor opt
        this.broadcastLokalModellOppdatert(ModelloppdateringType.SIGNATAR_SIGNERT, {
            dokumentId: dokumentId,
            signatarId: signatarId
        });
    }

    //dokumentannotering

    vedNyDokumentAnnotering(filter: (_: { dokumentAnnoteringId: string, dokumentVersjonId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        dokumentAnnoteringId: string;
        dokumentVersjonId: string;
    }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.NY_DOKUMENTANNOTERING);
    }

    vedDokumentAnnoteringSlettet(filter: (_: { dokumentAnnoteringId: string, dokumentVersjonId: string }) => boolean, scope: IScope, vedOppdatering: (_: {
        dokumentAnnoteringId: string;
        dokumentVersjonId: string;
    }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.DOKUMENTANNOTERING_SLETTET);
    }

    //kommentar

    //arkiv

    vedNyArkivnode(filter: (_: { arkivnodeId: string, mappe: boolean, forelderId: string }) => boolean, scope: ng.IScope, vedOppdatering: (_: {
        arkivnodeId: string;
        mappe: boolean;
        forelderId: string;
    }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.NY_ARKIVNODE);
    }

    broadcastLokalNyArkivnode(arkivnode: l.ArkivnodeModell<any>, forelderId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.NY_ARKIVNODE, {arkivnodeId: arkivnode.id, mappe: arkivnode.erMappe, forelderId: forelderId});
    }

    vedOppdatertArkivnode(filter: (_: { arkivnodeId: string, mappe: boolean, forelderId: string }) => boolean, scope: ng.IScope, vedOppdatering: (_: {
        arkivnodeId: string;
        mappe: boolean;
        forelderId: string;
    }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.ARKIVNODE_OPPDATERT);
    }

    broadcastLokalOppdatertArkivnode(arkivnode: l.ArkivnodeModell<any>, forelderId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.ARKIVNODE_OPPDATERT, {arkivnodeId: arkivnode.id, mappe: arkivnode.erMappe, forelderId: forelderId});
    }

    vedSlettetArkivnode(filter: (_: { arkivnodeId: string, mappe: boolean, navn: string, forelderId: string }) => boolean, scope: ng.IScope, vedOppdatering: (_: {
        arkivnodeId: string;
        mappe: boolean;
        navn: string;
        forelderId: string;
    }) => void) {
        this.registrerLytter(filter, scope, vedOppdatering, ModelloppdateringType.ARKIVNODE_SLETTET);
    }

    broadcastLokalSlettetArkivnode(arkivnodeId: string, mappe: boolean, navn: string, forelderId: string) {
        this.broadcastLokalModellOppdatert(ModelloppdateringType.ARKIVNODE_SLETTET, {arkivnodeId: arkivnodeId, mappe: mappe, navn: navn, forelderId: forelderId});
    }

    //hjelpemetoder

    registrerLytter<T>(filter: (modell: T) => boolean, scope: IScope, vedOppdatering: (modell: T) => void, event: string, evalueringstidspunkt: Evalueringstipdspunkt = Evalueringstipdspunkt.umiddelbart) {
        scope.$on(event, (event: IAngularEvent, modell: T) => {
            const lytter = () => {
                debug("Modelloppdatering", evalueringstidspunkt, event, filter(modell), modell)
                if (!filter(modell)) return;
                vedOppdatering(modell);
            };
            if (evalueringstidspunkt == Evalueringstipdspunkt.sent)
                this.$timeout(() => lytter());
            else
                lytter();
        })
    }


    private async registrerAbonnement() {
        const request = this.buildRequest();
        const handlers = this.buildHandlers();

        if (erNil(this.atmosphereInstans))
            this.atmosphereInstans = new Atmosphere({
                logLevel: 'debug',
                defaultTransport: 'websocket',
                fallbackTransport: undefined
            });

        await this.atmosphereInstans.subscribe(request, handlers);
        this.atmosphereLukket = false;
    }

    private async terminerAbonnement(){
        this.atmosphereLukket = true;
        await this.atmosphereInstans.closeAll();
        this.atmosphereInstans = null;
    }

    /**
     * Konfigurerer Atmosphere request for WebSocket-tilkobling.
     */
    private buildRequest(): AtmosphereRequest {
        const stdReconnectInterval = 2 * 1000; //forsøker å reconnecte hver 2. sekund, merk at denne vil øke eksponentielt mellom gjentakende reconnect forsøkt
        const maxReconnectOnClose = 15; //prøv å koble til på nytt i ca 15 timer

        //autentisering: cookies blir inkludert i handshake GET kallet - autentiserer dermed vhja token lagret i cookie
        return {
            url: `${this.konfigurasjon.serviceRotUrl}/api/ws/modelloppdatert`,
            contentType: 'application/json',
            transport: 'websocket',
            //atmosphere vil forsøke å re-connecte dersom subscription blir lukket
            //dette skjer uavhengig av årsak - også direkte kall til Subscription.close vil føre til at re-connect trigges
            reconnect: true,
            //The interval in milliseconds before an attempt to reconnect will be made.
            reconnectInterval: stdReconnectInterval,
            //The maximum reconnect after a connection is marked as 'dead'. The failureToReconnect will get called after the maximum is reached.
            maxReconnectOnClose: maxReconnectOnClose,
            //withCredentials: true - denne blir ikke brukt når transport:'websocket'
        };
    }

    /**
     * Konfigurerer event handlers for Atmosphere subscription.
     */
    private buildHandlers(): SubscriptionHandlers<string> {

        return {
            /** Håndter melding pushet fra server */
            message: (response: AtmosphereResponse<string>) => {
                debug("message", response.responseBody, response)
                this.$rootScope.$apply(() => this.håndterMelding(response.responseBody));
            },

            reopen: (response: AtmosphereResponse) => {
                debug("reopen", response)
            },

            reconnect: (request: AtmosphereRequest, response: AtmosphereResponse) => {
                debug("reconnect", request, response)
            },

            error: (error: Error) => {
                this.$log.error(`Modelloppdatering.error: ${error.message}`, error);
            },

            transportFailure: (errorMsg: string, request: AtmosphereRequest) => {
                this.$log.error(`Modelloppdatering.transportFailure errorMsg:${errorMsg} request:${JSON.stringify(request)}`);
            },

            open: (response: AtmosphereResponse) => {
                debug("open", response)
            },

            close: (response: AtmosphereResponse) => {
                debug("close", response)
                //dersom vi eksplisitt har lukket subscriptionen vil vi ikke re-connecte
                if (this.atmosphereLukket) response.request.reconnect = false;
            },

            failureToReconnect: (request: AtmosphereRequest, response: AtmosphereResponse) => {
                debug("failureToReconnect", request, response)
            },

            clientTimeout: (request: AtmosphereRequest) => {
                debug("clientTimeout", request)
            }
        };
    }

    private håndterMelding(melding: string) {
        var oppdatering = this.parseMelding(melding);
        if (erNil(oppdatering)) return; //melding lot seg ikke parse..

        if (oppdatering.status !== 200) {
            this.avsluttAbonnement();
            debug(`håndterMelding: Modelloppdatering WS handshake avvist med status ${oppdatering.status}`);
            return;
        }

        if (this.erLokalOpprinnet(oppdatering)) {
            debug('håndterMelding: dropper melding da denne er lokalt opprinnet', oppdatering)
            return;
        }

        this.broadcastModellOppdatert(oppdatering.type, oppdatering.modell);
        this._hendelser.next(oppdatering); //rxjs har et WebSocketSubject - kanskje dette kunne vært brukt nå ajs er utfaset
    }

    private erLokalOpprinnet(oppdatering: l.Modelloppdatering) {
        if (_.isEmpty(oppdatering.klientId)) return false;
        return this.konfigurasjon.klientId === oppdatering.klientId;
    }

    private broadcastModellOppdatert(eventId: string, modell: any) {
        this.$rootScope.$broadcast(eventId, modell);
    }

    /** Konveterer mottatt melding som json streng til objekt.
     * Håndterer konvertering av datoer til Date.
     *
     * offentlig for testbarhet..*/
    parseMelding(jsonMelding: string): Modelloppdatering<any> {
        try {
            return konverterIso8601DatoStrengTilDate(JSON.parse(jsonMelding))
        } catch (err) {
            this.$log.error(`Feil under parsing av ws modelloppdatering melding, mottatt json:${jsonMelding}`);
            return null;
        }
    }
}

export enum Evalueringstipdspunkt { umiddelbart, sent}
